// src/components/ui/SectionTabs.jsx
//
// Barra de pestañas para las secciones que agrupan más de un objeto — Agents con
// sus avatares, Connections con sus credenciales.
//
// Es controlado a propósito: quien lo usa mantiene la pestaña activa en la URL,
// para que recargar no devuelva a la primera y para que un enlace a una pestaña
// concreta funcione. El `ui/tabs.jsx` que ya existe guarda el estado dentro, así
// que no sirve para eso.
import React from "react";
import { cn } from "../../lib/utils";

/**
 * @param {{key: string, label: string, Icon?: React.ComponentType, count?: number}[]} tabs
 * @param {string} active  key de la pestaña activa
 * @param {(key: string) => void} onChange
 */
export default function SectionTabs({ tabs, active, onChange, className = "", ariaLabel }) {
  if (!tabs?.length) return null;

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        "inline-flex items-center gap-1 rounded-xl border border-white/10 bg-white/[0.04] p-1",
        // Quien la monta a lo ancho la pone a desplazarse; la barra no se ve.
        "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className
      )}
    >
      {tabs.map((tab) => {
        const isActive = tab.key === active;
        const Icon = tab.Icon;
        return (
          <button
            key={tab.key}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.key)}
            className={cn(
              // `shrink-0`: dentro de un contenedor que se desplaza, sin esto
              // las pestañas se comprimen hasta partir sus etiquetas en vez de
              // salirse y dejar que se arrastre.
              "flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-all sm:px-3.5 sm:py-1.5",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70",
              isActive
                ? "bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg shadow-violet-500/20"
                : "text-gray-400 hover:bg-white/5 hover:text-white"
            )}
          >
            {Icon && <Icon className="h-3.5 w-3.5" />}
            {tab.label}
            {typeof tab.count === "number" && (
              <span
                className={cn(
                  "ml-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
                  isActive ? "bg-white/20 text-white" : "bg-white/[0.06] text-gray-400"
                )}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
